/**
 * Spec 089b — Settings → Integrations section for Search Console.
 *
 * Renders:
 *   - Status row (status, site_url, last_sync_at, last_sync_error)
 *   - Connect button (brand-owners + members only) — calls POST .../connect and
 *     does `window.location.assign(authorization_url)` to hand off to Google.
 *   - Disconnect button — calls DELETE .../connection after a confirm.
 *   - Property picker — mounted when `showPicker` is true (URL has
 *     `?gsc_connect=success`) and the connection's site_url is empty.
 *
 * Three-state behaviour:
 *   - flag-off:  banner ("Search Console integration is not enabled")
 *   - no conn:   Connect button + explainer
 *   - connected: status row + Disconnect + (optional) picker mount
 */

import { useCallback, useEffect, useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { apiFetch } from '../lib/api.js';
import { apiPost, apiDelete } from '../lib/api-mutations.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';
import { SearchConsolePropertyPicker } from './SearchConsolePropertyPicker.js';
import { SearchConsoleBackfillSection } from './SearchConsoleBackfillSection.js';

interface ConnectionSummary {
  status: 'active' | 'expired' | 'revoked' | 'error';
  site_url: string;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

export interface SearchConsoleIntegrationsSectionProps {
  brandId: string;
  /** Tenant id — needed for the admin-only backfill subsection (089e). */
  tenantId?: string | null;
  /** True if the URL has ?gsc_connect=success — triggers picker mount. */
  showPicker: boolean;
  onPickerComplete?: () => void;
}

type SectionState =
  | { kind: 'loading' }
  | { kind: 'flag-off' }
  | { kind: 'no-connection' }
  | { kind: 'connected'; connection: ConnectionSummary };

export function SearchConsoleIntegrationsSection({
  brandId,
  tenantId,
  showPicker,
  onPickerComplete,
}: SearchConsoleIntegrationsSectionProps) {
  const [state, setState] = useState<SectionState>({ kind: 'loading' });
  const [pickerOpen, setPickerOpen] = useState(showPicker);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { hasRole } = useAuth();
  const toast = useToast();

  const canMutate =
    hasRole('brand-owner') ||
    hasRole('brand-member') ||
    hasRole('orbit:admin') ||
    hasRole('orbit:developer');

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await apiFetch(`/api/v1/brands/${brandId}/search-console/connection`);
    if (res.status === 503) {
      setState({ kind: 'flag-off' });
      return;
    }
    if (res.status === 404) {
      setState({ kind: 'no-connection' });
      return;
    }
    if (!res.ok) {
      setState({ kind: 'no-connection' });
      return;
    }
    const connection = (await res.json()) as ConnectionSummary;
    setState({ kind: 'connected', connection });
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Sync picker visibility with prop changes (deep links from OAuth callback).
  useEffect(() => {
    if (showPicker) setPickerOpen(true);
  }, [showPicker]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const body = await apiPost<{ authorization_url: string }>(
        `/api/v1/brands/${brandId}/search-console/connect`,
        {},
      );
      window.location.assign(body.authorization_url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.addToast(`Connect failed: ${msg.slice(0, 120)}`, 'error');
      setConnecting(false);
    }
  }, [brandId, toast]);

  const handleDisconnect = useCallback(async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Disconnect Search Console for this brand?\n\n' +
        'This will delete the stored connection locally. To revoke the grant in your ' +
        'Google account, also visit https://myaccount.google.com/permissions.',
    );
    if (!confirmed) return;
    setDisconnecting(true);
    try {
      await apiDelete(`/api/v1/brands/${brandId}/search-console/connection`);
      toast.addToast('Disconnected. Remember to also revoke at Google.', 'success');
      void load();
    } catch (err) {
      toast.addToast(`Disconnect failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      setDisconnecting(false);
    }
  }, [brandId, toast, load]);

  const handlePickerComplete = useCallback(() => {
    setPickerOpen(false);
    toast.addToast('Search Console property bound to this brand', 'success');
    onPickerComplete?.();
    void load();
  }, [load, onPickerComplete, toast]);

  // ─── Render ────────────────────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <SectionShell>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner  />
          Loading…
        </div>
      </SectionShell>
    );
  }

  if (state.kind === 'flag-off') {
    return (
      <SectionShell>
        <div
          data-testid="gsc-settings-flag-off"
          className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <Icon name="bx-error" className="mt-0.5 flex-shrink-0 text-amber-700" />
          <p>
            Search Console integration is not enabled for this deployment. Contact your
            GEO Observatory admin to enable it.
          </p>
        </div>
      </SectionShell>
    );
  }

  if (state.kind === 'no-connection') {
    return (
      <SectionShell>
        <div
          data-testid="gsc-settings-not-connected"
          className="rounded-md border border-slate-200 bg-white p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="bx-plug" className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Not connected</span>
            </div>
            {canMutate && (
              <Button
                variant="primary"
                onClick={() => void handleConnect()}
                disabled={connecting}
                data-testid="gsc-settings-connect-btn"
              >
                {connecting ? <Spinner /> : <Icon name="bx-plug" />}
                Connect Search Console
              </Button>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Connecting lets GEO Observatory read this brand's AI Overview / AI Mode
            impressions and clicks from Google's Search Console. We never write
            to Search Console; the scope is read-only (`webmasters.readonly`).
          </p>
          {!canMutate && (
            <p className="mt-2 text-xs text-slate-400">
              You don't have permission to connect this brand. Ask a brand owner.
            </p>
          )}
        </div>
      </SectionShell>
    );
  }

  // ─── Connected ─────────────────────────────────────────────────
  const conn = state.connection;
  const statusOk = conn.status === 'active';
  return (
    <SectionShell>
      <div
        data-testid="gsc-settings-connected"
        className="rounded-md border border-slate-200 bg-white p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              {statusOk ? (
                <Icon name="bx-check-circle" className="text-green-600" />
              ) : (
                <Icon name="bx-x-circle" className="text-red-600" />
              )}
              <span className="text-sm font-medium text-slate-700">
                {statusOk ? 'Connected' : `Status: ${conn.status}`}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-500 sm:grid-cols-2">
              <div>
                <dt className="inline font-medium text-slate-600">Property: </dt>
                <dd
                  className="inline text-slate-700"
                  data-testid="gsc-settings-site-url"
                >
                  {conn.site_url || <span className="text-slate-400">not yet selected</span>}
                </dd>
              </div>
              <div>
                <dt className="inline font-medium text-slate-600">Last synced: </dt>
                <dd
                  className="inline text-slate-700"
                  data-testid="gsc-settings-last-sync"
                >
                  {conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : 'never'}
                </dd>
              </div>
              {conn.last_sync_error && (
                <div className="col-span-full">
                  <dt className="inline font-medium text-red-600">Last error: </dt>
                  <dd className="inline text-red-700">{conn.last_sync_error}</dd>
                </div>
              )}
            </dl>
          </div>
          {canMutate && (
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="danger"
                onClick={() => void handleDisconnect()}
                disabled={disconnecting}
                data-testid="gsc-settings-disconnect-btn"
              >
                {disconnecting ? <Spinner /> : <Icon name="bx-unlink" />}
                Disconnect
              </Button>
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
              >
                Revoke at Google
                <Icon name="bx-link-external" />
              </a>
            </div>
          )}
        </div>

        {/* Property picker — mounted after OAuth callback. Closes when complete. */}
        {pickerOpen && (conn.site_url === '' || showPicker) && (
          <SearchConsolePropertyPicker
            brandId={brandId}
            onComplete={handlePickerComplete}
            onCancel={() => setPickerOpen(false)}
          />
        )}
      </div>

      {/* Spec 089e — Backfill history subsection. Admin-only; non-admins see nothing. */}
      {hasRole('orbit:admin') && tenantId && conn.site_url !== '' && (
        <div className="mt-4">
          <SearchConsoleBackfillSection tenantId={tenantId} brandId={brandId} />
        </div>
      )}
    </SectionShell>
  );
}

function SectionShell({ children }: { children: React.ReactNode }) {
  return (
    <div data-testid="gsc-settings-section">
      <div className="flex items-center gap-2">
        <Icon name="bx-plug" className="text-violet-600" />
        <h3 className="text-base font-semibold text-slate-900">Google Search Console</h3>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Read AI Overview / AI Mode impressions from your verified Search Console
        property. Read-only — GEO Observatory never writes to Search Console.
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}
